/**
 * Create / edit a commitment.
 *
 * The form previews the first few due dates as you type. Schedules are the
 * part users get wrong — "the 31st, monthly" behaves differently in February —
 * and showing the actual dates beats explaining the rule.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/misc';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ResponsiveDialog } from '@/components/ui/dialog';
import { AmountInput } from '@/components/AmountInput';
import { AccountPicker } from '@/components/AccountPicker';
import { COLOR_TOKENS, colorClasses } from '@/lib/colors';
import { formatDate, todayIso, toCanonicalAmount } from '@/lib/format';
import { previewDates, KIND_LABELS, FREQUENCY_LABELS } from '@/lib/schedule';
import { useCategories, useCurrencies, useSaveCommitment } from '@/hooks/useApi';
import type { AccountRef, Commitment, CommitmentKind, Frequency } from '@/lib/types';

type Draft = {
  id?: number;
  name: string;
  kind: CommitmentKind;
  direction: 'INCOME' | 'EXPENSE';
  amountRaw: string;
  amountMinor: number | null;
  currency: string;
  frequency: Frequency;
  startDate: string;
  dayOfMonth: number | null;
  bound: 'count' | 'end' | 'open';
  totalInstalments: string;
  endDate: string;
  account: Partial<AccountRef>;
  categoryId: number | null;
  note: string;
  color: string;
};

function emptyDraft(): Draft {
  return {
    name: '',
    kind: 'CREDIT',
    direction: 'EXPENSE',
    amountRaw: '',
    amountMinor: null,
    currency: 'EUR',
    frequency: 'MONTHLY',
    startDate: todayIso(),
    dayOfMonth: new Date().getDate(),
    bound: 'count',
    totalInstalments: '',
    endDate: '',
    account: {},
    categoryId: null,
    note: '',
    color: 'slate',
  };
}

function draftFrom(c: Commitment): Draft {
  return {
    id: c.id,
    name: c.name,
    kind: c.kind,
    direction: c.direction,
    amountRaw: (c.amountMinor / 10 ** c.decimals).toFixed(c.decimals),
    amountMinor: c.amountMinor,
    currency: c.currency,
    frequency: c.frequency,
    startDate: c.startDate,
    dayOfMonth: c.dayOfMonth,
    bound: c.totalInstalments != null ? 'count' : c.endDate ? 'end' : 'open',
    totalInstalments: c.totalInstalments != null ? String(c.totalInstalments) : '',
    endDate: c.endDate ?? '',
    account:
      c.fundId && c.locationId
        ? { fundId: c.fundId, locationId: c.locationId, currency: c.currency }
        : {},
    categoryId: c.categoryId,
    note: c.note ?? '',
    color: c.color,
  };
}

export function CommitmentDialog({
  open,
  editing,
  onOpenChange,
}: {
  open: boolean;
  editing: Commitment | null;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const [d, setD] = useState<Draft>(emptyDraft);
  const { data: currencies = [] } = useCurrencies();
  const { data: categories = [] } = useCategories({
    kind: d.direction === 'INCOME' ? 'INCOME' : 'EXPENSE',
  });
  const save = useSaveCommitment();

  useEffect(() => {
    if (!open) return;
    setD(editing ? draftFrom(editing) : emptyDraft());
  }, [open, editing]);

  const decimals = currencies.find((c) => c.code === d.currency)?.decimals ?? 2;

  const preview = useMemo(
    () =>
      previewDates({
        frequency: d.frequency,
        startDate: d.startDate,
        dayOfMonth: d.dayOfMonth,
        totalInstalments: d.bound === 'count' ? Number(d.totalInstalments) || null : null,
        endDate: d.bound === 'end' ? d.endDate || null : null,
      }),
    [d.frequency, d.startDate, d.dayOfMonth, d.bound, d.totalInstalments, d.endDate],
  );

  const linked = Boolean(d.account.fundId && d.account.locationId);
  const canSave =
    d.name.trim().length > 0 &&
    d.amountMinor != null &&
    d.amountMinor > 0 &&
    preview.dates.length > 0 &&
    !preview.error;

  const handleSave = async () => {
    if (!canSave || d.amountMinor == null) return;
    try {
      await save.mutateAsync({
        id: d.id,
        name: d.name.trim(),
        kind: d.kind,
        direction: d.direction,
        amount: toCanonicalAmount(d.amountMinor, decimals),
        currency: d.currency,
        frequency: d.frequency,
        startDate: d.startDate,
        dayOfMonth: d.frequency === 'WEEKLY' || d.frequency === 'ONE_OFF' ? null : d.dayOfMonth,
        totalInstalments: d.bound === 'count' ? Number(d.totalInstalments) || null : null,
        endDate: d.bound === 'end' ? d.endDate || null : null,
        fundId: linked ? d.account.fundId : null,
        locationId: linked ? d.account.locationId : null,
        categoryId: d.categoryId,
        note: d.note || undefined,
        color: d.color,
      });
      toast.success(d.id ? 'Записано' : 'Задължението е добавено');
      onOpenChange(false);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const periodic = d.frequency !== 'ONE_OFF';

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title={d.id ? 'Промяна' : 'Ново регулярно движение'}
      description="Кредит, лизинг, абонамент, застраховка — или регулярен приход."
      className="sm:max-w-lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-2">
          {(
            [
              ['EXPENSE', 'Плащам'],
              ['INCOME', 'Получавам'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() =>
                setD((p) => ({
                  ...p,
                  direction: value,
                  categoryId: null,
                  kind: value === 'INCOME' ? 'SALARY' : 'CREDIT',
                }))
              }
              className={cn(
                'h-11 rounded-md border text-sm font-medium transition-colors',
                d.direction === value
                  ? value === 'INCOME'
                    ? 'border-income/30 bg-income/15 text-income'
                    : 'border-expense/30 bg-expense/15 text-expense'
                  : 'text-muted-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cm-name">Име</Label>
          <Input
            id="cm-name"
            value={d.name}
            onChange={(e) => setD((p) => ({ ...p, name: e.target.value }))}
            placeholder={d.direction === 'INCOME' ? 'напр. Заплата' : 'напр. Лизинг Toyota'}
            autoFocus
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Вид</Label>
            <Select
              value={d.kind}
              onValueChange={(v) => setD((p) => ({ ...p, kind: v as CommitmentKind }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(KIND_LABELS) as CommitmentKind[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {KIND_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-amount">Вноска</Label>
            <AmountInput
              id="cm-amount"
              value={d.amountRaw}
              onChange={(raw) => setD((p) => ({ ...p, amountRaw: raw }))}
              onValueChange={(m) => setD((p) => ({ ...p, amountMinor: m }))}
              currency={d.currency}
              decimals={decimals}
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Валута</Label>
            <Select
              value={d.currency}
              onValueChange={(v) => setD((p) => ({ ...p, currency: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Периодичност</Label>
            <Select
              value={d.frequency}
              onValueChange={(v) => setD((p) => ({ ...p, frequency: v as Frequency }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(FREQUENCY_LABELS) as Frequency[]).map((f) => (
                  <SelectItem key={f} value={f}>
                    {FREQUENCY_LABELS[f]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cm-start">Първо плащане</Label>
            <Input
              id="cm-start"
              type="date"
              value={d.startDate}
              onChange={(e) =>
                setD((p) => ({
                  ...p,
                  startDate: e.target.value,
                  dayOfMonth: e.target.value ? Number(e.target.value.slice(8, 10)) : p.dayOfMonth,
                }))
              }
            />
          </div>
        </div>

        {periodic ? (
          <div className="space-y-2 rounded-md border p-3">
            <Label>Докога</Label>
            <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
              {(
                [
                  ['count', 'Брой вноски'],
                  ['end', 'Крайна дата'],
                  ['open', 'Безсрочно'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setD((p) => ({ ...p, bound: value }))}
                  className={cn(
                    'rounded-sm px-2 py-1.5 text-xs font-medium transition-colors',
                    d.bound === value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            {d.bound === 'count' ? (
              <Input
                type="number"
                min={1}
                max={600}
                inputMode="numeric"
                value={d.totalInstalments}
                onChange={(e) => setD((p) => ({ ...p, totalInstalments: e.target.value }))}
                placeholder="напр. 60"
              />
            ) : d.bound === 'end' ? (
              <Input
                type="date"
                value={d.endDate}
                min={d.startDate}
                onChange={(e) => setD((p) => ({ ...p, endDate: e.target.value }))}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Без край — за заплата или абонамент. Графикът се удължава сам.
              </p>
            )}
          </div>
        ) : null}

        {/* Seeing the real dates beats explaining the clamping rule. */}
        {preview.error ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {preview.error}
          </p>
        ) : preview.dates.length > 0 ? (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
            <span className="text-muted-foreground">Първи падежи: </span>
            <span className="tabular">
              {preview.dates.slice(0, 4).map(formatDate).join(' · ')}
              {preview.dates.length > 4 ? ' …' : ''}
            </span>
            {preview.total != null ? (
              <div className="mt-1 text-muted-foreground">
                Общо {preview.total} вноски, последна{' '}
                <span className="tabular">{formatDate(preview.dates[preview.dates.length - 1]!)}</span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-2 rounded-md border p-3">
          <Label>Плаща се от</Label>
          <AccountPicker
            value={d.account}
            onChange={(next) => setD((p) => ({ ...p, account: next, currency: next.currency ?? p.currency }))}
            idPrefix="cm"
          />
          <p className="text-xs text-muted-foreground">
            {linked
              ? 'Потвърждаването на вноска ще запише реална операция и ще намали салдото.'
              : 'Остави празно, ако излиза от сметка, която не следиш тук — тогава е само за планиране и не пипа салда.'}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Категория</Label>
            <Select
              value={d.categoryId ? String(d.categoryId) : 'none'}
              onValueChange={(v) =>
                setD((p) => ({ ...p, categoryId: v === 'none' ? null : Number(v) }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Без категория</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Цвят</Label>
            <div className="flex flex-wrap gap-1.5">
              {COLOR_TOKENS.map((token) => (
                <button
                  key={token}
                  type="button"
                  aria-label={token}
                  onClick={() => setD((p) => ({ ...p, color: token }))}
                  className={cn(
                    'h-6 w-6 rounded-full ring-offset-2 ring-offset-background transition-all',
                    colorClasses(token).bg,
                    d.color === token && 'ring-2 ring-foreground',
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cm-note">Бележка</Label>
          <Textarea
            id="cm-note"
            rows={2}
            value={d.note}
            onChange={(e) => setD((p) => ({ ...p, note: e.target.value }))}
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Откажи
          </Button>
          <Button className="flex-1" disabled={!canSave || save.isPending} onClick={() => void handleSave()}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Запиши
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}
